// Dependencies
import { Suspense, lazy } from "react";
import Box from "@mui/material/Box";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
} from "react-router";

import { DataCacheProvider } from "@/contexts/DataCacheProvider";

// MSAL
import { MsalProvider, MsalContext } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";

// Contexts
import AppThemeProvider from "@/styles/AppThemeProvider";
import { NotificationContextProvider } from "@/contexts/NotificationContextProvider";
import { SelectedAccountProvider } from "@/contexts/SelectedAccountContext";
import { BreadcrumbLabelProvider } from "@/contexts/BreadcrumbLabelContext";

// Global Components
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { PageLayout } from "@/components/PageLayout";
import { BreadcrumbCommon } from "@/components/BreadcrumbCommon";

// Hooks
import { useNetwork } from "@/hooks/useNetwork";
import { useCurrentUser } from "@/hooks/useProfileData"; // Make sure to import this

// Guards
import { RouteGuard } from "@/components/RouteGuard";

// Components
import OutageWrapper from "@/components/OutageWrapper";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// Styles
import "styles/App.css";

// Pages
const SignIn = lazy(() => import("./pages/SignIn"));
const SignOut = lazy(() => import("./pages/SignOut"));
const Home = lazy(() => import("./pages/Home"));
const Profile = lazy(() => import("./pages/Profile"));
const PaymentHistory = lazy(() => import("./pages/Profile/PaymentHistory"));
const BrokerageAndTnc = lazy(() => import("./pages/BrokerageAndTnc"));
const BrokerageDetail = lazy(() => import("./pages/Brokerage"));
const TncDetail = lazy(() => import("./pages/Tnc"));
const TncUpdate = lazy(() => import("./pages/Tnc/Update"));
const BrokerageUpdate = lazy(() => import("./pages/Brokerage/Update"));
const NewBrokerageTncPage = lazy(() => import("./pages/BrokerageAndTnc/New"));
const Company = lazy(() => import("./pages/Company"));
const InspectionStation = lazy(() => import("./pages/InspectionStation"));
const InspectionDetail = lazy(() => import("./pages/InspectionStation/Detail"));
const InspectionUpdate = lazy(() => import("./pages/InspectionStation/Update"));
const NewInspection = lazy(() => import("./pages/InspectionStation/New"));

const Mechanics = lazy(() => import("./pages/Mechanics"));
const MechanicDetail = lazy(() => import("./pages/Mechanics/Detail"));
const NewMechanic = lazy(() => import("./pages/Mechanics/New"));
const Plates = lazy(() => import("./pages/Plates"));
const ApplyPlate = lazy(() => import("./pages/Plates/Apply"));
const PlateDetail = lazy(() => import("./pages/Plates/Detail"));

// Routes that should have a scrollable header (not fixed)
const SCROLLABLE_HEADER_ROUTES = [
  "/brokerages/",
  "/tncs/",
  "/inspectionstations/",
  "/mechanics/",
  "/plates/",
];

const BREADCRUMB_ROUTES = [
  "/company",
  "/brokeragestncs",
  "/brokerages",
  "/tncs",
  "/inspectionstations",
  "/mechanics",
  "/plates",
];

// NEW: Define the wrapper component that handles layout, loading
const LayoutAndErrorWrapper = () => {
  useNetwork();
  const { isLoading } = useCurrentUser();

  const location = useLocation();
  const showBreadcrumb = BREADCRUMB_ROUTES.some((route) =>
    location.pathname.startsWith(route),
  );

  // Determine if the current route should have a fixed header
  // Check if the current pathname starts with any of the scrollable header routes
  const fixedHeader = !SCROLLABLE_HEADER_ROUTES.some((route) =>
    location.pathname.startsWith(route),
  );

  // Handle the initial user loading state
  if (isLoading) {
    return (
      <PageLayout fixedHeader={fixedHeader}>
        <LoadingIndicator />
      </PageLayout>
    );
  }

  // If no error, render the normal layout with child routes
  return (
    <PageLayout fixedHeader={fixedHeader}>
      <ErrorBoundary>
        <Suspense fallback={<LoadingIndicator />}>
          {showBreadcrumb ? (
            <Box
              sx={{
                mb: 2,
              }}
            >
              <BreadcrumbCommon
                overrides={{
                  "/brokeragestncs": "Brokerages and TNCs",
                  "/brokerages": "Brokerages and TNCs",
                  "/tncs": "Brokerages and TNCs",
                  "/inspectionstations": "Inspection Stations",
                  "/mechanics": "Mechanics",
                  "/plates": "Plates",
                }}
                linkOverrides={{
                  "/": "/home",
                  "/brokerages": "/brokeragestncs",
                  "/tncs": "/brokeragestncs",
                  brokerages: "/brokeragestncs",
                  tncs: "/brokeragestncs",
                }}
              />
            </Box>
          ) : null}
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </PageLayout>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/signout" element={<SignOut />} />
      <Route element={<OutageWrapper />}>
        <Route element={<LayoutAndErrorWrapper />}>
          <Route path="/" element={<SignIn />} />
          <Route
            path="/profile"
            element={
              <RouteGuard requireEstablishedContact>
                <Profile />
              </RouteGuard>
            }
          />
          <Route
            path="/paymenthistory"
            element={
              <RouteGuard requireEstablishedContact>
                <PaymentHistory />
              </RouteGuard>
            }
          />
          <Route
            path="/home"
            element={
              <RouteGuard>
                <Home />
              </RouteGuard>
            }
          />
          <Route
            path="/company"
            element={
              <RouteGuard requireCompany={true}>
                <Company />
              </RouteGuard>
            }
          />
          <Route
            path="/brokeragestncs"
            element={
              <RouteGuard requireCompany={true}>
                <BrokerageAndTnc />
              </RouteGuard>
            }
          />
          <Route
            path="/brokerages/:id"
            element={
              <RouteGuard requireCompany={true}>
                <BrokerageDetail />
              </RouteGuard>
            }
          />
          <Route
            path="/brokerages/:id/update"
            element={
              <RouteGuard requireCompany={true}>
                <BrokerageUpdate />
              </RouteGuard>
            }
          />
          <Route
            path="/tncs/:id"
            element={
              <RouteGuard requireCompany={true}>
                <TncDetail />
              </RouteGuard>
            }
          />
          <Route
            path="/tncs/:id/update"
            element={
              <RouteGuard requireCompany={true}>
                <TncUpdate />
              </RouteGuard>
            }
          />
          <Route
            path="/brokeragestncs/new"
            element={
              <RouteGuard requireCompany={true}>
                <NewBrokerageTncPage />
              </RouteGuard>
            }
          />
          <Route
            path="/inspectionstations"
            element={
              <RouteGuard requireCompany={true}>
                <InspectionStation />
              </RouteGuard>
            }
          />
          <Route
            path="/inspectionstations/:id"
            element={
              <RouteGuard requireCompany={true}>
                <InspectionDetail />
              </RouteGuard>
            }
          />
          <Route
            path="/inspectionstations/:id/update"
            element={
              <RouteGuard requireCompany={true}>
                <InspectionUpdate />
              </RouteGuard>
            }
          />
          <Route
            path="/inspectionstations/new"
            element={
              <RouteGuard requireCompany={true}>
                <NewInspection />
              </RouteGuard>
            }
          />
          <Route
            path="/mechanics"
            element={
              <RouteGuard requireCompany={false}>
                <Mechanics />
              </RouteGuard>
            }
          />
          <Route
            path="/mechanics/me"
            element={
              <RouteGuard requireCompany={false}>
                <MechanicDetail />
              </RouteGuard>
            }
          />
          <Route
            path="/mechanics/:id"
            element={
              <RouteGuard requireCompany={false}>
                <MechanicDetail />
              </RouteGuard>
            }
          />
          <Route
            path="/mechanics/new"
            element={
              <RouteGuard requireCompany={true}>
                <NewMechanic />
              </RouteGuard>
            }
          />

          <Route
            path="/plates/apply"
            element={
              <RouteGuard>
                <ApplyPlate />
              </RouteGuard>
            }
          />

          <Route
            path="/plates/apply/:id"
            element={
              <RouteGuard>
                <ApplyPlate />
              </RouteGuard>
            }
          />

          <Route
            path="/plates"
            element={
              <RouteGuard>
                <Plates />
              </RouteGuard>
            }
          />
          <Route
            path="/plates/:id"
            element={
              <RouteGuard>
                <PlateDetail />
              </RouteGuard>
            }
          />

          <Route path="*" element={<Navigate to="/" />} />
        </Route>
      </Route>
    </>,
  ),
);

const App = ({ instance }: { instance: PublicClientApplication }) => {
  return (
    <AppThemeProvider>
      <NotificationContextProvider>
        <MsalProvider instance={instance}>
          <DataCacheProvider>
            <SelectedAccountProvider>
              <BreadcrumbLabelProvider>
                <ReactQueryDevtools />
                <RouterProvider router={router} />
              </BreadcrumbLabelProvider>
            </SelectedAccountProvider>
          </DataCacheProvider>
        </MsalProvider>
      </NotificationContextProvider>
    </AppThemeProvider>
  );
};

// make msalContext available to other components
App.msalContext = MsalContext;

export default App;
